import { useMemo, useRef, useState, useEffect } from 'react';
import { CoreBox } from '@youscience/core';
import {
  FinalReportDocument,
  CounselorFormStatus,
  FinalReportSchema,
  FormNames,
} from '@youscience/commonapp-service-common';

import useRecommenderWorkspaceStore from '@store/RecommenderWorkspaceStore';
import useNotificationStore from '@store/NotificationStore';

import useRecommenderStore from '@store/RecommenderStore';

import { TabSections } from '@interfaces/recommenderWorkspace';

import { FormState } from '@constants/commonApp/types';

import { ConfirmationMessage } from '@components/ConfirmationMessage';

import { useApplicantForm } from '@pages/StudentDetailsPage/hooks/useApplicantForm';
import { useApplicantFormsMetadata } from '@pages/StudentDetailsPage/hooks/useApplicantFormsMetadata';

import { formatDateWithFallback } from '@utils/formatter';

import { useUserProfile } from '@hooks/useUserProfile';

import WarningIcon from '@images/modal-warning.svg?react';

import Submissions from '../../common/Submissions';
import FormHeader from '../../common/FormHeader';
import FormFooter from '../../common/FormFooter';
import ReviewPdf, { ReviewTypes } from '../../common/ReviewPdf';
import {
  checkCounselorProfileValidity,
  checkFormValidity,
  PREVIEW_ERROR_NOTIFICATION,
  REVIEW_NOT_READY_ERROR_NOTIFICATION,
  onSaveErrorNotification,
  onSaveSuccessNotification,
  onSubmitSuccessNotification,
  onUnsubmitSuccessNotification,
} from '../../common/formUtils';
import ClassRank, { ClassRankFields } from './components/ClassRank';
import GPA, { GPAFields } from './components/GPA';
import Transcripts, { TranscriptsFields } from './components/Transcripts';
import Summary, { SummaryFields } from './components/Summary';

import { sxStyles } from './FinalReport.styles';

import { useRemoveShowErrorOnUnmount, removeShowErrorsFromUrl } from '@utils/navigation';
import { FormAvailabilityStatus, FormCompletionStatus } from '../../common/enums';

const formName = FormNames.FINAL_REPORT;

export const FinalReport = () => {
  useRemoveShowErrorOnUnmount();

  const { notify } = useNotificationStore((state) => state);
  const { userId } = useUserProfile();
  const { recommenderProfile } = useRecommenderStore((store) => store);
  const finalReport = useApplicantForm<FinalReportDocument & { memberId: number }>(formName);
  const { formsMetadata } = useApplicantFormsMetadata();
  const initialFormState = useMemo(
    () => ({
      [TabSections.ClassRank]: finalReport.content?.classRank,
      [TabSections.GPA]: finalReport.content?.gpa,
      [TabSections.Transcripts]: finalReport.content?.transcripts,
      [TabSections.Summary]: finalReport.content?.summary,
    }),
    [finalReport.content],
  );
  const form = useRef(initialFormState);
  const [formErrors, setFormErrors] = useState({});
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isOpenConfirmationMessage, setIsOpenConfirmationMessage] = useState(false);
  const [reviewType, setReviewType] = useState<ReviewTypes>(ReviewTypes.PREVIEW_FORM);
  const [expandedSection, setExpandedSection] = useState<TabSections | null>(TabSections.ClassRank);
  const isShowErrors = new URLSearchParams(window.location.search).get('showErrors') === 'true';

  const { applicantId, recommenderId } = finalReport.metadata;
  const isSubmitted =
    finalReport.metadata?.completionStatus === FormCompletionStatus.SUBMITTED ||
    finalReport.metadata?.completionStatus === FormCompletionStatus.DOWNLOADED;
  const isSubmitDisabled = finalReport.metadata?.availabilityStatus === FormAvailabilityStatus.NOT_AVAILABLE;
  const isRecommendationSubmitted =
    formsMetadata[FormNames.RECOMMENDATION]?.completionStatus === FormCompletionStatus.SUBMITTED ||
    formsMetadata[FormNames.RECOMMENDATION]?.completionStatus === FormCompletionStatus.DOWNLOADED;

  const {
    applicant: { members },
  } = useRecommenderWorkspaceStore((store) => store);

  const requiredCounselorRecommendationColleges = members.reduce((acc, member) => {
    if (
      member.counselor.forms?.some(
        (form) => form.type === FormNames.RECOMMENDATION && form.status === CounselorFormStatus.REQUIRED,
      )
    ) {
      acc.push(member.collegeName ?? '');
    }

    return acc;
  }, [] as string[]);

  const isRecommendationRequired =
    !isRecommendationSubmitted && formsMetadata[FormNames.RECOMMENDATION]?.requiredStatus;

  const toggleExpandedSelection = (section: TabSections) => {
    if (expandedSection === section) {
      setExpandedSection(null);
    } else {
      setExpandedSection(section);
    }
  };

  const getNextSection = (section: TabSections): TabSections | undefined => {
    switch (section) {
      case TabSections.ClassRank:
        return TabSections.GPA;
      case TabSections.GPA:
        return TabSections.Transcripts;
      case TabSections.Transcripts:
        return TabSections.Summary;
      case TabSections.Summary:
        return undefined;
    }
  };

  const onFormChangeHandler =
    <K,>(section: TabSections) =>
    (sectionFormState: FormState<K>) => {
      form.current = { ...form.current, [section]: sectionFormState.data };
    };

  const validateForm = () => {
    const formValid = checkFormValidity(form.current, FinalReportSchema);
    const profileValid = checkCounselorProfileValidity(recommenderProfile);

    return {
      form: !formValid,
      recommenderProfile: !profileValid.personal,
      schoolProfile: !profileValid.school,
    };
  };

  const handleSaveForLater = async (options?: {
    isHideNotification?: boolean;
    section?: keyof typeof initialFormState;
  }) => {
    const { isHideNotification, section } = options || {};
    const data = section ? { [section]: form.current[section] } : form.current;
    await finalReport.save({ ...data, applicantId, recommenderId });

    if (!isHideNotification) {
      finalReport.isError ? onSaveErrorNotification() : onSaveSuccessNotification(formName);
    }

    if (section && !finalReport.isError) {
      toggleExpandedSelection(section);
      const nextSection = getNextSection(section);
      if (nextSection) toggleExpandedSelection(nextSection);
    }
  };

  const previewForm = async () => {
    if (isSubmitted) {
      setIsPreviewOpen(true);
      await finalReport.getSubmitted({ applicantId, recommenderId });
    } else {
      await handleSaveForLater({ isHideNotification: true });

      const formErrors = validateForm();
      setFormErrors(formErrors);
      setIsPreviewOpen(true);
      const hasFormErrors = Object.values(formErrors).some(Boolean);

      if (!hasFormErrors) {
        removeShowErrorsFromUrl();
        await finalReport.preview({ ...form.current, applicantId, recommenderId });
      }
    }

    useRecommenderWorkspaceStore.subscribe((state) => {
      if (state.isError) {
        notify(isSubmitted ? REVIEW_NOT_READY_ERROR_NOTIFICATION : PREVIEW_ERROR_NOTIFICATION);
        setIsPreviewOpen(false);
      }
    });
  };

  const submitForm = async (signature: string, signedDate: string, memberIds: number[]) => {
    await Promise.all(
      memberIds.map((memberId) =>
        finalReport.submit({
          applicantId,
          recommenderId,
          memberId,
          counselorApproval: { isReviewed: true, isAffirmed: true, signature, signedDate, userId: userId as string },
        }),
      ),
    );

    finalReport.isError ? onSaveErrorNotification() : onSubmitSuccessNotification(formName);
  };

  const handleOpenConfirmationMessage = async () => {
    setIsOpenConfirmationMessage(true);
  };

  const handleOnSubmit = async () => {
    setReviewType(ReviewTypes.REVIEW_AND_SUBMIT);
    void previewForm();
  };

  const handleOnPreview = () => {
    setReviewType(isSubmitted ? ReviewTypes.REVIEW_FORM : ReviewTypes.PREVIEW_FORM);
    void previewForm();
  };

  const handleOnUnsubmit = async () => {
    await finalReport.unsubmit({ applicantId, recommenderId });

    finalReport.isError ? onSaveErrorNotification() : onUnsubmitSuccessNotification(formName);
  };

  useEffect(() => {
    if (isShowErrors) {
      const errors = validateForm();
      setFormErrors(errors);

      const sections = [TabSections.ClassRank, TabSections.GPA, TabSections.Transcripts, TabSections.Summary] as const;

      const firstErrorSection = sections.find((section) => {
        const sectionSchema = FinalReportSchema.shape[section];
        const sectionData = form.current[section];
        const validation = sectionSchema.safeParse(sectionData);
        return !validation.success;
      });

      if (firstErrorSection) {
        setExpandedSection(firstErrorSection);
      }
    }
  }, [isShowErrors]);

  if (!finalReport.isReady) {
    return null;
  }

  return (
    <CoreBox sx={sxStyles.pageWrap}>
      <FormHeader
        name="Final report"
        tooltipTitle="The school report shares information about your student's academic history. It's also where you provide their most up-to-date transcripts."
        deadline={formatDateWithFallback(finalReport.metadata?.deadline)}
        status={finalReport.metadata?.completionStatus || FormCompletionStatus.NEW}
        onPreviewClick={handleOnPreview}
        onSaveForLaterClick={handleSaveForLater}
        onSubmitClick={isRecommendationRequired ? handleOpenConfirmationMessage : handleOnSubmit}
        onUnsubmitClick={handleOnUnsubmit}
        areAllActionsDisabled={finalReport.isSavingForm}
        isFormSumbitted={isSubmitted}
        isSubmitDisabled={isSubmitDisabled}
        formName={formName}
      />
      {isSubmitted ? (
        <Submissions
          formName={formName}
          onSubmit={isRecommendationRequired ? handleOpenConfirmationMessage : handleOnSubmit}
        />
      ) : (
        <>
          <ClassRank
            classRank={finalReport.content?.classRank || recommenderProfile?.schoolProfile?.classRank}
            isExtended={expandedSection === TabSections.ClassRank}
            onExpand={() => toggleExpandedSelection(TabSections.ClassRank)}
            onFormChange={onFormChangeHandler<ClassRankFields>(TabSections.ClassRank)}
            onContinue={() => handleSaveForLater({ section: TabSections.ClassRank })}
            isSaved={finalReport.sectionIsSaved.classRank}
            showError={
              isShowErrors && !FinalReportSchema.shape.classRank.safeParse(form.current[TabSections.ClassRank]).success
            }
          />

          <GPA
            gpa={finalReport.content?.gpa || recommenderProfile?.schoolProfile?.gpa}
            isExtended={expandedSection === TabSections.GPA}
            onExpand={() => toggleExpandedSelection(TabSections.GPA)}
            onFormChange={onFormChangeHandler<GPAFields>(TabSections.GPA)}
            onContinue={() => {
              handleSaveForLater({ section: TabSections.GPA });
            }}
            isSaved={finalReport.sectionIsSaved.gpa}
            showError={isShowErrors && !FinalReportSchema.shape.gpa.safeParse(form.current[TabSections.GPA]).success}
          />

          <Transcripts
            transcripts={
              finalReport.content?.transcripts || (recommenderProfile?.schoolProfile?.transcripts as TranscriptsFields)
            }
            isExtended={expandedSection === TabSections.Transcripts}
            onExpand={() => toggleExpandedSelection(TabSections.Transcripts)}
            onFormChange={onFormChangeHandler<TranscriptsFields>(TabSections.Transcripts)}
            onContinue={() => handleSaveForLater({ section: TabSections.Transcripts })}
            isSaved={finalReport.sectionIsSaved.transcripts}
            applicantName={finalReport.metadata?.studentName}
            showError={
              isShowErrors &&
              !FinalReportSchema.shape.transcripts.safeParse(form.current[TabSections.Transcripts]).success
            }
          />

          <Summary
            summary={finalReport.content?.summary}
            isExtended={expandedSection === TabSections.Summary}
            onExpand={() => toggleExpandedSelection(TabSections.Summary)}
            onFormChange={onFormChangeHandler<SummaryFields>(TabSections.Summary)}
            isSaved={finalReport.sectionIsSaved.summary}
            applicantName={finalReport.metadata.studentName}
            showError={
              isShowErrors && !FinalReportSchema.shape.summary.safeParse(form.current[TabSections.Summary]).success
            }
          />

          <FormFooter
            onPreviewClick={handleOnPreview}
            onSaveForLaterClick={handleSaveForLater}
            onSubmitClick={isRecommendationRequired ? handleOpenConfirmationMessage : handleOnSubmit}
            onUnsubmitClick={handleOnUnsubmit}
            areAllActionsDisabled={finalReport.isSavingForm}
            isSubmitDisabled={isSubmitDisabled}
            isFormSumbitted={isSubmitted}
            formName={formName}
          />
        </>
      )}

      {isOpenConfirmationMessage ? (
        <ConfirmationMessage
          icon={<WarningIcon />}
          title="Recommendation Not Submitted"
          subtitle={`You did not submit Counselor Recommendation form. It will be locked after submitting the Final Report, but it is required by ${
            requiredCounselorRecommendationColleges.length > 1
              ? 'multiple colleges'
              : requiredCounselorRecommendationColleges[0]
          }.`}
          goNextButtonTitle="Continue"
          isOpenConfirmationMessage={isOpenConfirmationMessage}
          setIsOpenConfirmationMessage={setIsOpenConfirmationMessage}
          onGoNextBtnClick={handleOnSubmit}
        />
      ) : null}

      {isPreviewOpen ? (
        <ReviewPdf
          title="Final report"
          subtitle="Please review the PDF preview of your form. Scroll to the bottom if necessary to confirm and continue."
          isOpen={isPreviewOpen}
          isLoading={finalReport.isLoadingPreview}
          reviewType={reviewType}
          pdf={finalReport.previewPdfURL}
          onClose={() => setIsPreviewOpen(false)}
          onContinue={(signature?: string, date?: string, memberIds?: number[]) => {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            if (reviewType === ReviewTypes.REVIEW_AND_SUBMIT) submitForm(signature!, date!, memberIds!);
          }}
          members={finalReport.metadata.members
            .filter((member) => !member.submittedDate && !member.downloadedDate)
            .map((member) => ({ name: member.memberName, id: member.memberId }))}
          errors={formErrors}
        />
      ) : null}
    </CoreBox>
  );
};
